import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CONNECTION_EVENT, WRITE_TIMEOUT_MESSAGE } from '@tradesmatepro/shared-ui/utils/resilientFetch';

/**
 * Connection problems: time out and retry (PRODUCT_DECISIONS, 2026-10-23). Shows what the
 * shared Supabase fetch (packages/shared-ui/utils/resilientFetch.js) reports:
 *   retrying    — a read timed out and is being retried on its own
 *   failed      — a read still failed after its retries; Retry reloads the page
 *   unconfirmed — a save timed out and may or may not have gone through. Stays until
 *                 dismissed; the form stays open, so its own Save button is the retry.
 * A good response from the server clears retrying/failed. Styled like Toast.js.
 */
const ConnectionBanner = () => {
  const [state, setState] = useState(null);

  useEffect(() => {
    const onEvent = (ev) => {
      const next = ev.detail?.state;
      setState((prev) => {
        if (next === 'ok') return prev === 'unconfirmed' ? prev : null;
        // An unconfirmed save outranks read trouble: it is the message the user must act on.
        if (prev === 'unconfirmed' && next !== 'unconfirmed') return prev;
        return next;
      });
    };
    window.addEventListener(CONNECTION_EVENT, onEvent);
    return () => window.removeEventListener(CONNECTION_EVENT, onEvent);
  }, []);

  if (!state) return null;

  const isWarning = state === 'retrying';
  const message = state === 'retrying'
    ? 'Connection problem — retrying…'
    : state === 'unconfirmed'
      ? WRITE_TIMEOUT_MESSAGE
      : "Couldn't reach the server.";

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-9999 w-[calc(100%-2rem)] max-w-xl pointer-events-none">
      <div
        role={isWarning ? 'status' : 'alert'}
        aria-live={isWarning ? 'polite' : 'assertive'}
        aria-atomic="true"
        data-testid="connection-banner"
        data-state={state}
        className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg ${
          isWarning ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}
      >
        <ExclamationTriangleIcon className={`w-5 h-5 flex-shrink-0 ${isWarning ? 'text-yellow-500' : 'text-red-500'}`} aria-hidden="true" />
        <span className="flex-1 text-sm font-medium">{message}</span>
        {state === 'failed' && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm font-semibold underline hover:no-underline flex-shrink-0"
          >
            Retry
          </button>
        )}
        {state !== 'retrying' && (
          <button
            type="button"
            onClick={() => setState(null)}
            aria-label="Dismiss"
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ConnectionBanner;
